import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { fmtDate, fmtRelative, PRIORITY_LABELS, RETURN_TYPE_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, Tag, Truck, FileText, MessageSquarePlus, Trash2, ExternalLink, Image as ImageIcon, PackageCheck, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { restockFromReturn } from "@/lib/inventory";
import ReturnShipmentCard from "@/pages/returns/ReturnShipmentCard";

const ACTION_LABELS = {
  "return.created": "Cerere creată",
  "return.update": "Modificat",
  "return.status_change": "Status schimbat",
  "return.note_added": "Notă adăugată",
  "return.delete": "Șters",
};

export default function ReturnDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [ret, setRet] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteBody, setNoteBody] = useState("");

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get(`/returns/${id}`), api.get("/returns/statuses")]);
      setRet(r.data);
      setStatuses(s.data);
      if (hasPermission("manage_users")) {
        try { const u = await api.get("/users"); setUsers(u.data); } catch {}
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const updateField = async (patch) => {
    try {
      await api.patch(`/returns/${id}`, patch);
      toast.success("Salvat");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await api.post(`/returns/${id}/notes`, { body: noteBody });
      setNoteBody("");
      toast.success("Notă adăugată");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/returns/${id}/notes/${noteId}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteReturn = async () => {
    try {
      await api.delete(`/returns/${id}`);
      toast.success("Retur șters");
      navigate("/retururi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-96 lg:col-span-2" />
        <Skeleton className="h-96" />
      </div>
    </div>
  );

  if (!ret) return <div className="text-center text-muted-foreground py-12">Retur inexistent.</div>;

  const status = ret.status;
  const totalItems = ret.items?.reduce((a, b) => a + (b.quantity || 0), 0);

  return (
    <div className="space-y-6" data-testid="return-detail-page">
      <Link to="/retururi" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
        <ArrowLeft className="h-3.5 w-3.5" /> Înapoi la retururi
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="font-mono text-xs text-muted-foreground">{ret.return_number}</div>
            {status && (
              <Badge className="border" style={{ color: status.color, background: status.color + "15", borderColor: status.color + "30" }}>
                {status.name}
              </Badge>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{ret.customer_name}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />#{ret.order_number}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fmtDate(ret.created_at)}</span>
            <span>· {totalItems} {totalItems === 1 ? "produs" : "produse"}</span>
            <span>· {RETURN_TYPE_LABELS[ret.return_type]}</span>
          </div>
        </div>

        {/* Quick status changer */}
        <div className="flex items-center gap-2 flex-wrap">
          {ret.inventory_restock_done ? (
            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/30" data-testid="return-restock-done">
              <PackageCheck className="h-3 w-3 mr-1" /> Restock {ret.inventory_restock_qty || 0} buc
            </Badge>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-9 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10"
                onClick={async () => {
                  if (!window.confirm("Marchezi marfa ca recepționată și o adaugi înapoi în stoc?")) return;
                  const lines = (ret.items || []).map((it) => ({
                    shopify_product_id: it.product_id,
                    quantity: it.quantity || 1,
                  }));
                  const tid = toast.loading("Adaug în stoc...");
                  try {
                    const r = await restockFromReturn({ return_id: ret.id, lines, notes: `Retur #${ret.return_number || ret.id}` });
                    toast.success(`Restock OK: ${r.lines} produse · ${r.total_qty} buc`, { id: tid });
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Eroare", { id: tid });
                  }
                }}
                disabled={!hasPermission("edit_returns")}
                data-testid="return-restock-btn"
              >
                <PackageCheck className="h-3.5 w-3.5 mr-1.5" /> Recepționat (restock)
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 text-red-600 border-red-500/40 hover:bg-red-500/10"
                onClick={async () => {
                  if (!window.confirm("Marchezi marfa ca defectă? Stocul NU va fi afectat.")) return;
                  try {
                    await api.put(`/returns/${ret.id}`, { defective: true });
                    toast.success("Marcat ca defect");
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Eroare");
                  }
                }}
                disabled={!hasPermission("edit_returns")}
                data-testid="return-defective-btn"
              >
                <AlertOctagon className="h-3.5 w-3.5 mr-1.5" /> Defect
              </Button>
            </>
          )}
          <Select value={ret.status_id} onValueChange={(v) => updateField({ status_id: v })} disabled={!hasPermission("edit_returns")}>
            <SelectTrigger className="w-[180px]" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                    {s.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ret.priority} onValueChange={(v) => updateField({ priority: v })} disabled={!hasPermission("edit_returns")}>
            <SelectTrigger className="w-[140px]" data-testid="priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
            </SelectContent>
          </Select>
          {hasPermission("delete_returns") && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 text-red-600 border-red-500/40 hover:bg-red-500/10"
                  data-testid="return-delete-btn"
                  title="Șterge cererea de retur"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="return-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Ștergi cererea de retur?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Această acțiune este permanentă. Cererea <span className="font-mono font-semibold">{ret.return_number}</span> de la <strong>{ret.customer_name}</strong> va fi eliminată definitiv, împreună cu notele asociate.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="return-delete-cancel">Anulează</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteReturn}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="return-delete-confirm"
                  >
                    Șterge definitiv
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          {/* Products */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Produse returnate</h3>
            <div className="divide-y divide-border">
              {ret.items?.map((item) => (
                <div key={item.line_item_id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                  {item.image ? (
                    <img src={item.image} alt={item.title} className="h-14 w-14 rounded-md object-cover bg-secondary" />
                  ) : (
                    <div className="h-14 w-14 rounded-md bg-secondary flex items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.variant_title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm tabular-nums">×{item.quantity}</div>
                    <div className="text-xs text-muted-foreground font-mono">{item.price} RON</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Files */}
          {ret.files?.length > 0 && (
            <Card className="p-6 bg-card border-border">
              <h3 className="font-display font-semibold text-lg mb-4">Atașamente client ({ret.files.length})</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {ret.files.map((f) => {
                  const isVideo = f.content_type?.startsWith("video/");
                  const src = `${API_BASE}/files/${f.id}`;
                  return (
                    <a key={f.id} href={src} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-md overflow-hidden bg-secondary border border-border block group">
                      {isVideo ? (
                        <video src={src} className="w-full h-full object-cover" />
                      ) : (
                        <img src={src} alt={f.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      )}
                    </a>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Reason & notes */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Motivul returnării</h3>
            <p className="text-sm leading-relaxed mb-4">{ret.reason}</p>
            {ret.customer_notes && (
              <>
                <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Observații client</h4>
                <p className="text-sm leading-relaxed text-muted-foreground">{ret.customer_notes}</p>
              </>
            )}
          </Card>

          {/* Internal notes */}
          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold text-lg mb-4">Note interne</h3>
            {hasPermission("edit_returns") && (
              <div className="flex gap-2 mb-4">
                <Textarea
                  placeholder="Adaugă o notă internă vizibilă doar echipei…"
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  rows={2}
                  data-testid="note-input"
                />
                <Button onClick={addNote} disabled={!noteBody.trim()} data-testid="add-note-btn">
                  <MessageSquarePlus className="h-4 w-4" />
                </Button>
              </div>
            )}
            {ret.notes?.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Nicio notă încă.</div>}
            <div className="space-y-3">
              {ret.notes?.map((n) => (
                <div key={n.id} className="flex gap-3 group" data-testid={`note-${n.id}`}>
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">
                      {(n.author_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{n.author_name}</span>
                      <span className="text-xs text-muted-foreground">{fmtRelative(n.created_at)}</span>
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{n.body}</p>
                  </div>
                  {n.author_id === user?.id && (
                    <button onClick={() => deleteNote(n.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT 1/3 */}
        <div className="space-y-6">
          {/* Customer */}
          <Card className="p-6 bg-card border-border">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">Date client</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-xs">{ret.email}</span></div>
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-xs">{ret.phone}</span></div>
              {ret.shopify_order?.shipping_address && (
                <div className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5" /><span className="text-xs leading-relaxed">{ret.shopify_order.shipping_address.address1}, {ret.shopify_order.shipping_address.city}</span></div>
              )}
            </div>
          </Card>

          {/* Refund details */}
          {ret.return_type === "refund" && (
            <Card className="p-6 bg-card border-border">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">Date rambursare</h3>
              <div className="text-xs text-muted-foreground">IBAN</div>
              <div className="font-mono text-sm mb-3">{ret.iban || "—"}</div>
              <div className="text-xs text-muted-foreground">Bancă</div>
              <div className="text-sm">{ret.bank || "—"}</div>
            </Card>
          )}

          {/* Shopify order */}
          {ret.shopify_order && (
            <Card className="p-6 bg-card border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Comandă Shopify</h3>
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono font-medium">{ret.shopify_order.total_price} {ret.shopify_order.currency}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status plată</span><span>{ret.shopify_order.financial_status}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status livrare</span><span>{ret.shopify_order.fulfillment_status}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Plasată</span><span className="text-xs">{fmtDate(ret.shopify_order.created_at)}</span></div>
              </div>
            </Card>
          )}

          {/* AWB / courier — new reverse-logistics flow */}
          <ReturnShipmentCard returnId={id} onAfterChange={load} />

          {/* Timeline */}
          <Card className="p-6 bg-card border-border">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Istoric</h3>
            <div className="space-y-3">
              {ret.timeline?.length === 0 && <div className="text-xs text-muted-foreground">Niciun eveniment.</div>}
              {ret.timeline?.map((t) => (
                <div key={t.id} className="flex gap-3" data-testid={`timeline-${t.id}`}>
                  <div className="relative">
                    <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-full bg-border" />
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="text-xs font-medium">{ACTION_LABELS[t.action] || t.action}</div>
                    <div className="text-xs text-muted-foreground">{t.actor_name || "Sistem"} · {fmtRelative(t.created_at)}</div>
                    {t.details?.from && t.details?.to && (
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">{t.details.from} → {t.details.to}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
